import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  X,
  Wifi,
  Server,
  Key,
  Hash,
  Globe,
  CheckCircle2,
  AlertCircle,
  Loader2,
} from 'lucide-react'
import type { PrinterProtocol, PrinterConnectionConfig } from '@/lib/printerConnection.types'
import { PROTOCOL_INFO } from '@/lib/printerAdapters'
import { usePrinterConnectionStore } from '@/stores/printerConnectionStore'

interface AddPrinterModalProps {
  isOpen: boolean
  onClose: () => void
}

export default function AddPrinterModal({ isOpen, onClose }: AddPrinterModalProps) {
  const [step, setStep] = useState<'protocol' | 'config' | 'test'>('protocol')
  const [selectedProtocol, setSelectedProtocol] = useState<PrinterProtocol | null>(null)
  const [name, setName] = useState('')
  const [host, setHost] = useState('')
  const [port, setPort] = useState('')
  const [apiKey, setApiKey] = useState('')
  const [accessCode, setAccessCode] = useState('')
  const [serial, setSerial] = useState('')
  const [useTls, setUseTls] = useState(false)
  const [testing, setTesting] = useState(false)
  const [testResult, setTestResult] = useState<'success' | 'error' | null>(null)
  const [testError, setTestError] = useState('')

  const { addPrinter, connectPrinter } = usePrinterConnectionStore()

  const resetForm = () => {
    setStep('protocol')
    setSelectedProtocol(null)
    setName('')
    setHost('')
    setPort('')
    setApiKey('')
    setAccessCode('')
    setSerial('')
    setUseTls(false)
    setTesting(false)
    setTestResult(null)
    setTestError('')
  }

  const handleClose = () => {
    resetForm()
    onClose()
  }

  const handleProtocolSelect = (protocol: PrinterProtocol) => {
    setSelectedProtocol(protocol)
    const info = PROTOCOL_INFO[protocol]
    if (info) {
      setPort(String(info.defaultPort))
    }
    setStep('config')
  }

  const handleTestConnection = async () => {
    if (!selectedProtocol) return

    setTesting(true)
    setTestResult(null)
    setTestError('')

    const config: PrinterConnectionConfig = {
      id: `printer-${Date.now()}`,
      name: name || `${PROTOCOL_INFO[selectedProtocol]?.name || 'Unknown'} Printer`,
      protocol: selectedProtocol,
      host,
      port: port ? parseInt(port) : undefined,
      apiKey: apiKey || undefined,
      accessCode: accessCode || undefined,
      serial: serial || undefined,
      useTls,
      autoReconnect: true,
      pollInterval: 2000,
    }

    try {
      // Add printer temporarily for testing
      addPrinter(config)
      await connectPrinter(config.id)
      setTestResult('success')
      setStep('test')
    } catch (error) {
      setTestResult('error')
      setTestError(error instanceof Error ? error.message : 'Connection failed')
    } finally {
      setTesting(false)
    }
  }

  const handleSave = () => {
    // Printer was already added during test, just close
    handleClose()
  }

  const protocolInfo = selectedProtocol ? PROTOCOL_INFO[selectedProtocol] : null

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
          onClick={handleClose}
        >
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            className="bg-surface-900 border border-surface-700 rounded-xl shadow-2xl w-full max-w-lg mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-surface-700">
              <h2 className="text-lg font-semibold text-white">Add Printer</h2>
              <button
                onClick={handleClose}
                className="p-1 rounded-lg hover:bg-surface-700 text-surface-400 hover:text-white transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Content */}
            <div className="p-6">
              {step === 'protocol' && (
                <div className="space-y-4">
                  <p className="text-surface-300 text-sm">
                    Select your printer's connection type:
                  </p>
                  <div className="grid gap-3">
                    {(Object.entries(PROTOCOL_INFO) as [PrinterProtocol, typeof PROTOCOL_INFO['octoprint']][]).map(
                      ([key, info]) => (
                        <button
                          key={key}
                          onClick={() => handleProtocolSelect(key)}
                          className="flex items-start gap-4 p-4 rounded-lg border border-surface-700 hover:border-primary-500 hover:bg-surface-800 transition-all text-left group"
                        >
                          <div className="p-2 rounded-lg bg-surface-800 group-hover:bg-primary-500/20">
                            <Server className="w-5 h-5 text-primary-400" />
                          </div>
                          <div className="flex-1">
                            <h3 className="font-medium text-white">{info.name}</h3>
                            <p className="text-sm text-surface-400 mt-0.5">
                              {info.description}
                            </p>
                            <div className="flex flex-wrap gap-1.5 mt-2">
                              {info.features.slice(0, 3).map((feature) => (
                                <span
                                  key={feature}
                                  className="px-2 py-0.5 text-xs rounded-full bg-surface-800 text-surface-300"
                                >
                                  {feature}
                                </span>
                              ))}
                            </div>
                          </div>
                        </button>
                      )
                    )}
                  </div>
                </div>
              )}

              {step === 'config' && protocolInfo && (
                <div className="space-y-4">
                  <button
                    onClick={() => setStep('protocol')}
                    className="text-sm text-primary-400 hover:text-primary-300 flex items-center gap-1"
                  >
                    ← Back to protocol selection
                  </button>

                  <div className="space-y-4">
                    {/* Name */}
                    <div>
                      <label className="block text-sm font-medium text-surface-300 mb-1.5">
                        Printer Name
                      </label>
                      <input
                        type="text"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder={`My ${protocolInfo.name} Printer`}
                        className="w-full px-3 py-2 rounded-lg bg-surface-800 border border-surface-700 text-white placeholder-surface-500 focus:outline-none focus:border-primary-500"
                      />
                    </div>

                    {/* Host */}
                    <div>
                      <label className="block text-sm font-medium text-surface-300 mb-1.5">
                        <Globe className="w-4 h-4 inline mr-1" />
                        Host / IP Address
                      </label>
                      <input
                        type="text"
                        value={host}
                        onChange={(e) => setHost(e.target.value)}
                        placeholder="192.168.1.100 or printer.local"
                        className="w-full px-3 py-2 rounded-lg bg-surface-800 border border-surface-700 text-white placeholder-surface-500 focus:outline-none focus:border-primary-500"
                      />
                    </div>

                    {/* Port */}
                    <div>
                      <label className="block text-sm font-medium text-surface-300 mb-1.5">
                        <Hash className="w-4 h-4 inline mr-1" />
                        Port
                      </label>
                      <input
                        type="number"
                        value={port}
                        onChange={(e) => setPort(e.target.value)}
                        placeholder={String(protocolInfo.defaultPort)}
                        className="w-full px-3 py-2 rounded-lg bg-surface-800 border border-surface-700 text-white placeholder-surface-500 focus:outline-none focus:border-primary-500"
                      />
                    </div>

                    {/* API Key (OctoPrint) */}
                    {protocolInfo.requiresApiKey && (
                      <div>
                        <label className="block text-sm font-medium text-surface-300 mb-1.5">
                          <Key className="w-4 h-4 inline mr-1" />
                          API Key
                        </label>
                        <input
                          type="password"
                          value={apiKey}
                          onChange={(e) => setApiKey(e.target.value)}
                          placeholder="Found in OctoPrint settings"
                          className="w-full px-3 py-2 rounded-lg bg-surface-800 border border-surface-700 text-white placeholder-surface-500 focus:outline-none focus:border-primary-500"
                        />
                      </div>
                    )}

                    {/* Access Code (Bambu) */}
                    {protocolInfo.requiresAccessCode && (
                      <>
                        <div>
                          <label className="block text-sm font-medium text-surface-300 mb-1.5">
                            <Key className="w-4 h-4 inline mr-1" />
                            Access Code
                          </label>
                          <input
                            type="password"
                            value={accessCode}
                            onChange={(e) => setAccessCode(e.target.value)}
                            placeholder="Found on printer's display"
                            className="w-full px-3 py-2 rounded-lg bg-surface-800 border border-surface-700 text-white placeholder-surface-500 focus:outline-none focus:border-primary-500"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-surface-300 mb-1.5">
                            Serial Number
                          </label>
                          <input
                            type="text"
                            value={serial}
                            onChange={(e) => setSerial(e.target.value)}
                            placeholder="Printer serial number"
                            className="w-full px-3 py-2 rounded-lg bg-surface-800 border border-surface-700 text-white placeholder-surface-500 focus:outline-none focus:border-primary-500"
                          />
                        </div>
                      </>
                    )}

                    {/* TLS Toggle */}
                    <div className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        id="useTls"
                        checked={useTls}
                        onChange={(e) => setUseTls(e.target.checked)}
                        className="w-4 h-4 rounded border-surface-600 bg-surface-800 text-primary-500 focus:ring-primary-500"
                      />
                      <label htmlFor="useTls" className="text-sm text-surface-300">
                        Use secure connection (HTTPS/TLS)
                      </label>
                    </div>

                    {/* Test Error */}
                    {testResult === 'error' && (
                      <div className="flex items-center gap-2 p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400">
                        <AlertCircle className="w-5 h-5 flex-shrink-0" />
                        <span className="text-sm">{testError}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {step === 'test' && testResult === 'success' && (
                <div className="text-center py-6">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-500/20 flex items-center justify-center">
                    <CheckCircle2 className="w-8 h-8 text-green-400" />
                  </div>
                  <h3 className="text-lg font-medium text-white mb-2">
                    Connection Successful!
                  </h3>
                  <p className="text-surface-400 text-sm">
                    Your printer "{name || 'New Printer'}" has been connected and added.
                  </p>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-surface-700">
              <button
                onClick={handleClose}
                className="px-4 py-2 rounded-lg text-surface-300 hover:text-white hover:bg-surface-700 transition-colors"
              >
                {step === 'test' ? 'Close' : 'Cancel'}
              </button>
              
              {step === 'config' && (
                <button
                  onClick={handleTestConnection}
                  disabled={!host || testing}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg bg-primary-500 hover:bg-primary-600 text-white font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {testing ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Testing...
                    </>
                  ) : (
                    <>
                      <Wifi className="w-4 h-4" />
                      Test & Add Printer
                    </>
                  )}
                </button>
              )}

              {step === 'test' && testResult === 'success' && (
                <button
                  onClick={handleSave}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg bg-primary-500 hover:bg-primary-600 text-white font-medium transition-colors"
                >
                  <CheckCircle2 className="w-4 h-4" />
                  Done
                </button>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
